/* eslint-disable max-lines */
import { toJS } from 'mobx';

import { merge, SerializedData } from 'bernie-core';

import { getErrorString, Logger, NOOP_LOGGER, SystemEvent } from 'bernie-logger';

import {
  AllBatch1Template,
  AllBatch2Template,
  AllBatch3Template,
  AllBusaHomeTemplate,
  AllBusaSubTemplate,
  AllListicleBlock,
  AllMagazineArticle,
  AllMagazineHome,
  Sdk,
  AllNavigation,
  AllVrboBestTimeToBook,
  AllAllStudioTemplate,
  AllExpediaLive,
  AllOceanHotelHomeTemplate,
  AllOceanHotelSubTemplate,
} from 'src/common/__generated__/sanity/types';
import { OperationResult } from 'src/common/__generated__/api';
import { SANITY_SOURCE_EVENTS } from './events/source-events';

import apiDummyData from '../stores/feed/sampleData'; // Remove after fixed API functionality
import { SwitchLayoutGroupContext } from 'node_modules/framer-motion/dist';
import { sanityLocaleMap } from 'src/stores/helpers/sanityLocaleMap';
import { fetchWithTimeout } from 'src/plugins/utils/fetch';

const { withoutKeys } = merge;
export class SanityApiSource {
  private readonly api: Sdk;
  private readonly apiDraft: Sdk;
  private readonly logger: Logger;

  constructor(api: Sdk, apiDraft: Sdk, logger: Logger = NOOP_LOGGER) {
    this.api = api;
    this.apiDraft = apiDraft;
    this.logger = logger;
  }

  // Get the api based on the isDraft flag;
  public getApi(isDraft): Sdk {
    if (isDraft) {
      return this.apiDraft;
    } else {
      return this.api;
    }
  }

  public allBatch1Template(
    variables: AllBatch1Template.Variables,
    pageId: string,
  ): Promise<OperationResult<AllBatch1Template.Query>> {
    const requestOptions = { 'x-page-id': pageId };

    return this.api
      .allBatch1Template(variables, requestOptions)
      .catch(this.getErrorHandler(variables, SANITY_SOURCE_EVENTS.SANITY_FETCH_ERROR));
  }

  public allBatch2Template(
    variables: AllBatch2Template.Variables,
    pageId: string,
  ): Promise<OperationResult<AllBatch2Template.Query>> {
    const requestOptions = { 'x-page-id': pageId };

    return this.api
      .allBatch2Template(variables, requestOptions)
      .catch(this.getErrorHandler(variables, SANITY_SOURCE_EVENTS.SANITY_FETCH_ERROR));
  }

  public allBatch3Template(
    variables: AllBatch3Template.Variables,
    pageId: string,
  ): Promise<OperationResult<AllBatch3Template.Query>> {
    const requestOptions = { 'x-page-id': pageId };

    return this.api
      .allBatch3Template(variables, requestOptions)
      .catch(this.getErrorHandler(variables, SANITY_SOURCE_EVENTS.SANITY_FETCH_ERROR));
  }

  public allBusaTemplate(
    variables: AllBusaHomeTemplate.Variables,
    pageId: string,
  ): Promise<OperationResult<AllBusaHomeTemplate.Query>> {
    const requestOptions = { 'x-page-id': pageId };

    return this.api
      .allBusaHomeTemplate(variables, requestOptions)
      .catch(this.getErrorHandler(variables, SANITY_SOURCE_EVENTS.SANITY_FETCH_ERROR));
  }

  public async allBusaHomeWithListicleBlocksRequest(
    variables: AllBusaHomeTemplate.Variables,
    variablesListicleBlock: AllListicleBlock.Variables,
    pageId: string,
  ) {
    const requestOptions = { 'x-page-id': pageId };
    const batchRequests = [
      this.api.allBusaHomeTemplate(variables, requestOptions),
      this.api.allListicleBlock(variablesListicleBlock, requestOptions),
    ];

    try {
      const results = await Promise.all(batchRequests).catch(
        this.getErrorHandler(variables, SANITY_SOURCE_EVENTS.SANITY_FETCH_ERROR),
      );
      return results;
    } catch (error) {
      console.error('Error fetching batch data:', error);
      return this.getErrorHandler(variables, SANITY_SOURCE_EVENTS.SANITY_FETCH_ERROR);
    }
  }

  public async allBusaSubWithListicleFilterRequest(
    variables: AllBusaSubTemplate.Variables,
    variablesListicleBlock: AllListicleBlock.Variables,
    pageId: string,
  ) {
    const requestOptions = { 'x-page-id': pageId };
    const batchRequests = [
      this.api.allBusaSubTemplate(variables, requestOptions),
      this.api.allListicleBlock(variablesListicleBlock, requestOptions),
    ];

    try {
      const results = await Promise.all(batchRequests).catch(
        this.getErrorHandler(variables, SANITY_SOURCE_EVENTS.SANITY_FETCH_ERROR),
      );

      return results;
    } catch (error) {
      console.error('Error fetching batch data:', error);
      return this.getErrorHandler(variables, SANITY_SOURCE_EVENTS.SANITY_FETCH_ERROR);
    }
  }

  public allBusaSubTemplate(
    variables: AllBusaSubTemplate.Variables,
    pageId: string,
  ): Promise<OperationResult<AllBusaSubTemplate.Query>> {
    const requestOptions = { 'x-page-id': pageId };

    return this.api
      .allBusaSubTemplate(variables, requestOptions)
      .catch(this.getErrorHandler(variables, SANITY_SOURCE_EVENTS.SANITY_FETCH_ERROR));
  }

  public allMagazineHome(
    variables: AllMagazineHome.Variables,
    pageId: string,
  ): Promise<OperationResult<AllMagazineHome.Query>> {
    const requestOptions = { 'x-page-id': pageId };

    return this.api
      .allMagazineHome(variables, requestOptions)
      .catch(this.getErrorHandler(variables, SANITY_SOURCE_EVENTS.SANITY_FETCH_ERROR));
  }

  public async allMagazineHomeWithNaviagationRequest(
    variables: AllMagazineHome.Variables,
    pageId: string,
    isdraft?: string,
  ) {
    const requestOptions = { 'x-page-id': pageId };

    // Base on the isdraft flag, get the api
    const api = isdraft === '1' ? true : false;
    const getApi = this.getApi(api);

    const batchRequests = [getApi.allMagazineHome(variables, requestOptions)];

    try {
      const results = await Promise.all(batchRequests).catch(
        this.getErrorHandler(variables, SANITY_SOURCE_EVENTS.SANITY_FETCH_ERROR),
      );
      return results;
    } catch (error) {
      console.error('Error fetching batch data:', error);
      return this.getErrorHandler(variables, SANITY_SOURCE_EVENTS.SANITY_FETCH_ERROR);
    }
  }

  public allMagazineArticle(
    variables: AllMagazineArticle.Variables,
    pageId: string,
  ): Promise<OperationResult<AllMagazineArticle.Query>> {
    const requestOptions = { 'x-page-id': pageId };

    return this.api
      .allMagazineArticle(variables, requestOptions)
      .catch(this.getErrorHandler(variables, SANITY_SOURCE_EVENTS.SANITY_FETCH_ERROR));
  }

  public async allVrboBestTimeToBookWithNaviagationRequest(
    variables: AllVrboBestTimeToBook.Variables,
    navigationVariables: AllNavigation.Variables,
    pageId: string,
  ) {
    const requestOptions = { 'x-page-id': pageId };
    const batchRequests = [
      this.api.allVrboBestTimeToBook(variables, requestOptions),
      this.api.allNavigation(navigationVariables, requestOptions),
    ];

    try {
      const results = await Promise.all(batchRequests).catch(
        this.getErrorHandler(variables, SANITY_SOURCE_EVENTS.SANITY_FETCH_ERROR),
      );
      return results;
    } catch (error) {
      console.error('Error fetching batch data:', error);
      return this.getErrorHandler(variables, SANITY_SOURCE_EVENTS.SANITY_FETCH_ERROR);
    }
  }

  public async allMagazineArticleWithNaviagationRequest(
    variables: AllMagazineArticle.Variables,
    pageId: string,
    isdraft?: string,
  ) {
    const requestOptions = { 'x-page-id': pageId };

    // Base on the isdraft flag, get the api
    const api = isdraft === '1' ? true : false;
    const getApi = this.getApi(api);

    const batchRequests = [getApi.allMagazineArticle(variables, requestOptions)];

    try {
      const results = await Promise.all(batchRequests).catch(
        this.getErrorHandler(variables, SANITY_SOURCE_EVENTS.SANITY_FETCH_ERROR),
      );
      return results;
    } catch (error) {
      console.error('Error fetching batch data:', error);
      return this.getErrorHandler(variables, SANITY_SOURCE_EVENTS.SANITY_FETCH_ERROR);
    }
  }

  public allStudioTemplateRequest(
    variables: AllAllStudioTemplate.Variables,
    pageId: string,
  ): Promise<OperationResult<AllAllStudioTemplate.Query>> {
    const requestOptions = { 'x-page-id': pageId };

    return this.api
      .allAllStudioTemplate(variables, requestOptions)
      .catch(this.getErrorHandler(variables, SANITY_SOURCE_EVENTS.SANITY_FETCH_ERROR));
  }

  private getErrorHandler(variables: any, sourceEvent: SystemEvent) {
    return (error: any) => {
      this.logger.logEvent(sourceEvent, variables, getErrorString(error));
      // Throw the error to the next catch block since it is not resolved here
      throw error;
    };
  }

  /**
   * Override the default `toJson` to exclude the Apollo client.
   * If we don't include this, we will get a JSON serialization error.
   *
   * Creates a basic JSON object from the store, excluding
   * any fields provided in `ignoreAdditionalKeys`.
   */
  public toJSON(ignoreAdditionalKeys: string[] | string = []): SerializedData {
    if (!Array.isArray(ignoreAdditionalKeys)) {
      ignoreAdditionalKeys = ignoreAdditionalKeys.constructor === String ? [ignoreAdditionalKeys] : [];
    }

    const regexIgnorePrivate = /^_.+/;
    const simpleObject: any = toJS(this);
    const ignoreKeys: string[] = Object.keys(simpleObject)
      .filter((key) => regexIgnorePrivate.test(key) || typeof simpleObject[key] === 'function')
      .concat(ignoreAdditionalKeys, 'logger', 'api', 'headers', 'apiWithFederation');

    return withoutKeys(ignoreKeys).merge({}, simpleObject);
  }

  public allExpediaLive(
    variables: AllExpediaLive.Variables,
    pageId: string,
    isdraft?: string,
  ): Promise<OperationResult<AllExpediaLive.Query>> {
    const requestOptions = { 'x-page-id': pageId };

    // Base on the isdraft flag, get the api
    const api = isdraft === '1' ? true : false;
    const getApi = this.getApi(api);

    return getApi
      .allExpediaLive(variables, requestOptions)
      .catch(this.getErrorHandler(variables, SANITY_SOURCE_EVENTS.SANITY_FETCH_ERROR));
  }

  public async allExpediaLiveWithNaviagationRequest(
    variables: AllExpediaLive.Variables,
    pageId: string,
    isdraft?: string,
  ): Promise<OperationResult<AllExpediaLive.Query>> {
    const requestOptions = { 'x-page-id': pageId };

    // Base on the isdraft flag, get the api
    const api = isdraft === '1' ? true : false;
    const getApi = this.getApi(api);

    return getApi
      .allExpediaLive(variables, requestOptions)
      .catch(this.getErrorHandler(variables, SANITY_SOURCE_EVENTS.SANITY_FETCH_ERROR));
  }

  public async fetchCapsAPIData1(
    partner: string,
    templateName: string,
    feedName: string,
    locale: string,
    siteID: string,
  ): Promise<any> {
    const fetchOptions: RequestInit = {
      method: 'GET',
      redirect: 'manual',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    };
    const maxRetries = 3;
    let attempts = 0;
    let bffResponse;
    let fetchBffResponsePromise;

    const langID = sanityLocaleMap(locale);
    let country = 'home';
    let slug = '';
    if (templateName !== '') country = templateName.toLowerCase();
    if (feedName !== '') slug = feedName.toLowerCase();
    const url = `https://www.hotels.com/capsapi/sanity/goguides/${siteID}/${langID}${country ? `/${country}` : ''}${slug ? `/${slug}` : ''
      }`;

    while (attempts < maxRetries) {
      attempts++;
      fetchBffResponsePromise = fetchWithTimeout(url, fetchOptions, 0);

      try {
        bffResponse = await fetchBffResponsePromise;
        // Handle 400/500s, except timeouts
        if (bffResponse.status < 500) {
          break;
        }

        console.log(
          `View model: Error fetching CMS (attempt ${attempts}): ${bffResponse.status} ${bffResponse.statusText}: ${url}`,
        );
      } catch (bffError) {
        console.log(`Unexpected error when fetching view model: ${bffError.stack}`);
      }
    }

    if (bffResponse.status >= 400) {
      console.log(`Error response when fetching view model, response status: ${bffResponse.status}: ${url}`);
    }

    try {
      return await bffResponse.json();
    } catch (error) {
      console.log(`Failed to parse decoded JSON: ${error.stack}: ${url}`);
    }
  }


  public async fetchCapsAPIData(
    partner: string,
    templateName: string,
    feedName: string,
    locale: string,
    siteID: string,
  ): Promise<any> {
    const fetchOptions: RequestInit = {
      method: 'GET',
      redirect: 'manual',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Connection: 'keep-alive',
      },
    };

    const MAX_RETRIES = 3; // Maximum number of retries
    const BASE_DELAY = 1000; // Base delay in milliseconds for exponential backoff
    let attempts = 0;
    let bffResponse: Response | null = null;

    const langID = sanityLocaleMap(locale);
    const country = templateName ? templateName.toLowerCase() : 'home';
    const slug = feedName ? feedName.toLowerCase() : '';
    const url = `https://www.hotels.com/capsapi/sanity/goguides/${siteID}/${langID}${country ? `/${country}` : ''}${slug ? `/${slug}` : ''
      }`;

    while (attempts < MAX_RETRIES) {
      attempts++;
      try {
        const fetchBffResponsePromise = fetchWithTimeout(url, fetchOptions, 10000); // 10s timeout
        bffResponse = await fetchBffResponsePromise;

        if (bffResponse?.status === 429) {
          const retryAfter = bffResponse.headers.get('Retry-After');
          const retryDelay = retryAfter
            ? parseInt(retryAfter, 10) * 1000
            : Math.pow(2, attempts) * BASE_DELAY + Math.floor(Math.random() * 1000); // fallback with jitter

          console.warn(`Rate limited. Retry ${attempts}/${MAX_RETRIES} after ${retryDelay}ms`);
          await new Promise((resolve) => setTimeout(resolve, retryDelay));
          continue;
        }

        console.log('bffResponse?.status', bffResponse.status);

        if (bffResponse?.status < 401) {
          console.info(`Successfully fetched data on attempt ${attempts}`);
          break;
        }

        console.error(
          `View model: Error fetching CMS (attempt ${attempts}): ${bffResponse.status} ${bffResponse.statusText}: ${url}`,
        );
      } catch (bffError) {
        console.error(`Unexpected error when fetching view model: ${bffError.stack}`);
      }

      const delay = Math.pow(2, attempts) * BASE_DELAY; // backoff
      console.warn(`Retrying after ${delay}ms (attempt ${attempts}/${MAX_RETRIES})`);
      await new Promise((resolve) => setTimeout(resolve, delay));
    }

    if (!bffResponse || bffResponse.status >= 400) {
      console.error(`Error response when fetching view model, response status: ${bffResponse?.status}: ${url}`);
      return null;
    }

    try {
      return await bffResponse.json();
    } catch (error) {
      console.error(`Failed to parse decoded JSON: ${error.stack}: ${url}`);
      return null;
    }
  }


  public async oceanHotelRequest(
    type: 'home' | 'sub',
    variables: AllOceanHotelHomeTemplate.Variables | AllOceanHotelSubTemplate.Variables,
    pageId: string,
    isdraft?: string,
  ) {
    const requestOptions = { 'x-page-id': pageId };
    const api = isdraft === '1';
    const getApi = this.getApi(api);

    const apiMethod = type === 'home' ? getApi.allOceanHotelHomeTemplate : getApi.allOceanHotelSubTemplate;
    const batchRequests = [apiMethod(variables, requestOptions)];

    try {
      const results = await Promise.all(batchRequests).catch(
        this.getErrorHandler(variables, SANITY_SOURCE_EVENTS.SANITY_FETCH_ERROR),
      );
      return results;
    } catch (error) {
      console.error('Error fetching batch data:', error);
      return this.getErrorHandler(variables, SANITY_SOURCE_EVENTS.SANITY_FETCH_ERROR);
    }
  }
}
